import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Carrega Google Analytics (GA4), Google Tag Manager e Facebook Pixel
 * dinamicamente a partir de /api/settings. Tudo desligado no admin.
 *
 * - ga_tracking_id: G-XXXXXXX
 * - gtm_id:         GTM-XXXXXXX
 * - fb_pixel_id:    opcional
 */
export default function Analytics() {
    const location = useLocation();
    const cfgRef = useRef({ ga: null, gtm: null, fb: null, loaded: false });

    // Carregar config uma vez
    useEffect(() => {
        api.get("/settings")
            .then((r) => {
                const { ga_tracking_id, gtm_id, fb_pixel_id } = r.data || {};
                cfgRef.current.ga = ga_tracking_id || null;
                cfgRef.current.gtm = gtm_id || null;
                cfgRef.current.fb = fb_pixel_id || null;
                injectScripts();
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isAdminRoute = () =>
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/admin");

    const injectScripts = () => {
        if (cfgRef.current.loaded || isAdminRoute()) return;
        const { ga, gtm, fb } = cfgRef.current;

        // GA4 / Universal
        if (ga && !document.getElementById("ga-script")) {
            const s = document.createElement("script");
            s.id = "ga-script";
            s.async = true;
            s.src = `https://www.googletagmanager.com/gtag/js?id=${ga}`;
            document.head.appendChild(s);

            const inline = document.createElement("script");
            inline.id = "ga-inline";
            inline.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                window.gtag = gtag;
                gtag('js', new Date());
                gtag('config', '${ga}', { send_page_view: false });
            `;
            document.head.appendChild(inline);
        }

        // Google Tag Manager
        if (gtm && !document.getElementById("gtm-script")) {
            const inline = document.createElement("script");
            inline.id = "gtm-script";
            inline.innerHTML = `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtm}');
            `;
            document.head.appendChild(inline);

            // noscript fallback
            const ns = document.createElement("noscript");
            ns.id = "gtm-noscript";
            ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtm}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
            document.body.prepend(ns);
        }

        // Facebook Pixel
        if (fb && !document.getElementById("fb-pixel-script")) {
            const inline = document.createElement("script");
            inline.id = "fb-pixel-script";
            inline.innerHTML = `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window,document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '${fb}');
                fbq('track', 'PageView');
            `;
            document.head.appendChild(inline);
        }

        cfgRef.current.loaded = true;
    };

    // Page tracking on route change
    useEffect(() => {
        if (isAdminRoute()) return;
        const path = location.pathname + location.search;
        // External providers (GA, GTM, FB)
        if (cfgRef.current.loaded) {
            if (window.gtag && cfgRef.current.ga) {
                window.gtag("event", "page_view", {
                    page_path: path,
                    page_location: window.location.href,
                    page_title: document.title,
                });
            }
            if (window.fbq && cfgRef.current.fb) {
                window.fbq("track", "PageView");
            }
        }
        // Internal analytics (admin dashboard) — always tracks
        api.post("/track/pageview", {
            path,
            referrer: document.referrer || "",
            title: document.title || "",
        }).catch(() => {});
    }, [location.pathname, location.search]);

    return null;
}
